import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ChevronDown, ChevronUp } from "lucide-react";

const Decision = ({ id, onDecide }) => {
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-2 mt-3">
      <input data-testid={`approval-note-${id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the author (optional)" className="flex-1 h-9 rounded-full bg-[#07080A] border border-white/10 px-4 text-xs text-slate-200" />
      <button data-testid={`approve-${id}`} onClick={() => onDecide("approve", note)} className="h-9 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Approve</button>
      <button data-testid={`reject-${id}`} onClick={() => onDecide("reject", note)} className="h-9 px-4 rounded-full border border-rose-400/50 text-rose-300 hover:bg-rose-500/10 text-xs font-semibold inline-flex items-center gap-1"><X className="h-3.5 w-3.5" /> Reject</button>
    </div>
  );
};

const StoryItem = ({ s, status, decide }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`approval-story-${s.story_id}`} className="rounded-2xl border border-white/10 bg-[#0F1117] p-4">
      <div className="flex gap-4">
        <div className="h-24 w-18 w-[72px] rounded-lg overflow-hidden bg-white/5 flex-shrink-0">{s.cover_url && <img src={fileUrl(s.cover_url)} alt="" className="h-full w-full object-cover" />}</div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg text-white">{s.title}</div>
          <div className="text-[11px] font-mono text-slate-500">by {s.author_name} · {s.genre} · {s.is_human_written ? "Human-written" : "AI-assisted"} · {(s.chapters || []).length} ch · {s.gems_paid || 0} gems paid</div>
          <div className="text-xs text-slate-400 mt-1">{s.tagline}</div>
          <button data-testid={`approval-toggle-${s.story_id}`} onClick={() => setOpen(!open)} className="mt-2 text-[11px] font-mono uppercase tracking-widest text-amber-300 inline-flex items-center gap-1">{open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {open ? "Hide" : "Read"} chapters</button>
        </div>
      </div>
      {open && (
        <div className="mt-3 max-h-80 overflow-y-auto rounded-xl bg-[#07080A] border border-white/5 p-4 space-y-4">
          {(s.chapters || []).map((c, i) => (
            <div key={i}><div className="font-semibold text-sm text-amber-200 mb-1">{c.title}</div><p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{c.content}</p></div>
          ))}
        </div>
      )}
      {status === "pending" ? <Decision id={s.story_id} onDecide={(a, n) => decide("story", s.story_id, a, n)} /> : s.admin_note && <div className="text-[11px] text-slate-500 italic mt-2">Note: {s.admin_note}</div>}
    </div>
  );
};

const CharacterItem = ({ c, status, decide }) => (
  <div data-testid={`approval-character-${c.submission_id}`} className="rounded-2xl border border-white/10 bg-[#0F1117] p-4">
    <div className="flex gap-4">
      <div className="h-24 w-[72px] rounded-lg overflow-hidden bg-white/5 flex-shrink-0">{c.image_url && <img src={fileUrl(c.image_url)} alt="" className="h-full w-full object-cover" />}</div>
      <div className="min-w-0 flex-1">
        <div className="font-serif text-lg text-white">{c.name} <span className="text-xs text-rose-300 font-sans">· {c.trope}</span></div>
        <div className="text-[11px] font-mono text-slate-500">by {c.author_name} · {c.category} · {c.gender} · {c.rarity} · {c.gems_paid || 0} gems{c.ai_generated ? " · AI-drafted" : ""}</div>
        <div className="text-xs text-slate-300 mt-1">{c.tagline}</div>
        <div className="text-xs text-slate-400 mt-1">{c.bio}</div>
        <div className="text-xs text-slate-500 mt-1 italic">“{c.opening_line}” — {c.voice}</div>
      </div>
    </div>
    {status === "pending" ? <Decision id={c.submission_id} onDecide={(a, n) => decide("character", c.submission_id, a, n)} /> : c.admin_note && <div className="text-[11px] text-slate-500 italic mt-2">Note: {c.admin_note}</div>}
  </div>
);

export const ApprovalsPanel = () => {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState({ stories: [], characters: [], counts: {} });
  const load = () => api.get("/admin/approvals", { params: { status } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (kind, id, action, note) => {
    try {
      await api.post(`/admin/approvals/${kind}/${id}`, { action, note });
      toast.success(action === "approve" ? "Approved — now live" : "Rejected — author notified & refunded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div data-testid="admin-approvals" className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} data-testid={`approvals-filter-${s}`} onClick={() => setStatus(s)} className={`h-8 px-4 rounded-full text-xs font-mono uppercase tracking-widest border ${status === s ? "bg-amber-500 text-black border-amber-500" : "border-white/10 text-slate-400"}`}>{s}</button>
        ))}
        <span data-testid="approvals-pending-counts" className="ml-auto text-xs text-slate-400 self-center">Pending: {data.counts.stories || 0} stories · {data.counts.characters || 0} characters</span>
      </div>
      <section>
        <h3 className="font-mono text-[11px] uppercase tracking-widest text-amber-400 mb-3">Characters ({data.characters.length})</h3>
        <div className="grid gap-3">{data.characters.length ? data.characters.map((c) => <CharacterItem key={c.submission_id} c={c} status={status} decide={decide} />) : <div className="text-sm text-slate-500">Nothing here.</div>}</div>
      </section>
      <section>
        <h3 className="font-mono text-[11px] uppercase tracking-widest text-amber-400 mb-3">Stories ({data.stories.length})</h3>
        <div className="grid gap-3">{data.stories.length ? data.stories.map((s) => <StoryItem key={s.story_id} s={s} status={status} decide={decide} />) : <div className="text-sm text-slate-500">Nothing here.</div>}</div>
      </section>
    </div>
  );
};
